import { useCallback } from 'react';
import { type UseFormGetValues, type UseFormSetValue } from 'react-hook-form';

import { MAX_QUIZ_COUNT } from '../../../constant';
import { type BuildSchemaInputType } from '../../../form/schema';
import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import { aiSaveAnimation } from '../../../util/aiSaveAnimation';
import { createGenerationCompleteMessage } from '../store/aiBuilderMessageStream';
import {
  type AiBuilderMessage,
  type AiBuilderQuestionRef,
} from '../store/types';
import { adaptAiGeneratedQuizzesToBuildForm } from '../utils/adaptAiGeneratedQuizzesToBuildForm';
import { type AiBuilderChatCopy } from '../utils/aiBuilderChatCopy';
import { applyQuestionSnapshotReplace } from '../utils/aiBuilderQuestionSnapshot';
import {
  getCompletionScrollTargetQuizId,
  getQuizIdByJumpLinkNumber,
  getQuizIdByQuestionRef,
} from '../utils/aiBuilderQuizTargets';
import {
  type AiBuilderChatEvent,
  type AiBuilderChatQuestionSnapshotReplaceEvent,
} from '../utils/types';

/** AI 액션 전 스냅샷/복원용 퀴즈 목록 깊은 복사. */
const cloneQuizList = (quizList: BuildQuizListItem[]): BuildQuizListItem[] =>
  structuredClone(quizList);

type AiBuilderMutableRef<T> = { current: T };
type AppendAiBuilderAssistantMessage = (
  message: Extract<AiBuilderMessage, { kind: 'assistant' }>,
) => void;

type UseAiBuilderQuizListMutationsParams = {
  appendAssistantMessage: AppendAiBuilderAssistantMessage;
  copy: AiBuilderChatCopy;
  getValues: UseFormGetValues<BuildSchemaInputType>;
  lastQuizListSnapshotRef: AiBuilderMutableRef<BuildQuizListItem[] | null>;
  openQuestionCountLimitAlert: () => void;
  replaceQuizList: (nextQuizList: BuildQuizListItem[]) => void;
  setValue: UseFormSetValue<BuildSchemaInputType>;
};

const scrollQuizElementIntoView = (quizId: string) => {
  document.getElementById(quizId)?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });
};

export const useAiBuilderQuizListMutations = ({
  appendAssistantMessage,
  copy,
  getValues,
  lastQuizListSnapshotRef,
  openQuestionCountLimitAlert,
  replaceQuizList,
  setValue,
}: UseAiBuilderQuizListMutationsParams) => {
  const resetLastQuizListSnapshot = useCallback(() => {
    lastQuizListSnapshotRef.current = null;
  }, [lastQuizListSnapshotRef]);

  const setQuizListFromAiAction = useCallback(
    (nextQuizList: BuildQuizListItem[]) => {
      replaceQuizList(nextQuizList);
      setValue('quizList', nextQuizList, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [replaceQuizList, setValue],
  );

  const scrollQuizByQuestionRef = useCallback(
    (questionRef: AiBuilderQuestionRef) => {
      const quizId = getQuizIdByQuestionRef(getValues('quizList'), questionRef);

      if (!quizId) return;

      scrollQuizElementIntoView(quizId);
    },
    [getValues],
  );

  const replaceQuizListWithSnapshot = useCallback(
    ({
      currentQuizList,
      nextQuizList,
      animationQuizIds,
      scrollTargetId,
    }: {
      currentQuizList: BuildQuizListItem[];
      nextQuizList: BuildQuizListItem[];
      animationQuizIds?: string[];
      scrollTargetId?: string;
    }) => {
      if (lastQuizListSnapshotRef.current === null) {
        lastQuizListSnapshotRef.current = cloneQuizList(currentQuizList);
      }

      setQuizListFromAiAction(nextQuizList);

      if (!animationQuizIds || animationQuizIds.length === 0) return;

      aiSaveAnimation(animationQuizIds, {
        scrollTargetId,
      });
    },
    [lastQuizListSnapshotRef, setQuizListFromAiAction],
  );

  const applyGeneratedQuizzes = useCallback(
    (event: Extract<AiBuilderChatEvent, { type: 'applyGeneratedQuizzes' }>) => {
      const currentQuizList = getValues('quizList');

      if (currentQuizList.length + event.quizzes.length > MAX_QUIZ_COUNT) {
        openQuestionCountLimitAlert();
        appendAssistantMessage({
          id: `assistant-limit-${event.requestId}`,
          kind: 'assistant',
          content: copy.system.questionLimitAppendFailed,
        });
        return;
      }

      const generatedQuizList = adaptAiGeneratedQuizzesToBuildForm(
        event.quizzes,
        event.generationId,
      );
      const nextQuizList = [...currentQuizList, ...generatedQuizList];
      const generatedQuizIds = generatedQuizList.map(quiz => quiz.id);

      replaceQuizListWithSnapshot({
        currentQuizList,
        nextQuizList,
        animationQuizIds: generatedQuizIds,
        scrollTargetId: getCompletionScrollTargetQuizId(generatedQuizIds),
      });

      appendAssistantMessage(
        createGenerationCompleteMessage({
          copy,
          requestId: event.requestId,
          selectedLabels: event.selectedLabels,
          quizCount: generatedQuizIds.length,
        }),
      );
    },
    [
      appendAssistantMessage,
      copy,
      getValues,
      openQuestionCountLimitAlert,
      replaceQuizListWithSnapshot,
    ],
  );

  const replaceQuestionSnapshots = useCallback(
    (event: AiBuilderChatQuestionSnapshotReplaceEvent) => {
      const currentQuizList = getValues('quizList');
      const outcome = applyQuestionSnapshotReplace(event, {
        currentQuizList,
        maxQuizCount: MAX_QUIZ_COUNT,
      });

      switch (outcome.status) {
        case 'stale':
          appendAssistantMessage({
            id: `assistant-stale-question-list-${event.requestId}`,
            kind: 'assistant',
            content: copy.system.staleQuestionList,
          });
          return;
        case 'exceedsMaxQuizCount':
          openQuestionCountLimitAlert();
          appendAssistantMessage({
            id: `assistant-limit-${event.requestId}`,
            kind: 'assistant',
            content: copy.system.questionLimitApplyFailed,
          });
          return;
        case 'applied':
          replaceQuizListWithSnapshot({
            currentQuizList,
            nextQuizList: outcome.nextQuizList,
            animationQuizIds: outcome.animationQuizIds,
            scrollTargetId: outcome.scrollTargetQuizId,
          });
          return;
      }
    },
    [
      appendAssistantMessage,
      copy,
      getValues,
      openQuestionCountLimitAlert,
      replaceQuizListWithSnapshot,
    ],
  );

  const focusQuizByNumber = useCallback(
    (quizNumber: number) => {
      const quizId = getQuizIdByJumpLinkNumber(
        getValues('quizList'),
        quizNumber,
      );

      if (!quizId) return;

      scrollQuizElementIntoView(quizId);
    },
    [getValues],
  );

  return {
    applyGeneratedQuizzes,
    focusQuizByNumber,
    replaceQuestionSnapshots,
    resetLastQuizListSnapshot,
    scrollQuizByQuestionRef,
  };
};
